import { Link, useSearchParams, useLocation } from "react-router";
import { LoadingSpinner } from "@kubdee/ui/custom/feedback/loading-spinner";
import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  LogOut,
  User,
  ChevronsUpDown,
  Home,
  Grid2x2,
  Building2,
  MapPin,
  Moon,
  Sun,
  Check,
  Settings as SettingsIcon,
} from "lucide-react";
import { getModuleIcon, getModuleColor } from "@kubdee/ui/lib/icon-registry";
import { MODULES } from "@kubdee/shared/module-ids";
import { useTheme } from "./theme-provider";
import { useAuth } from "@kubdee/sdk/hooks/use-auth";
import { useAuthStore } from "@kubdee/sdk/stores/auth-store";
import {
  useCompanies,
  useBranches,
  useCompanyModules,
  usePlatformModules,
  useTenantModules,
} from "@kubdee/sdk/hooks/use-settings";
import { useAllowedBranches } from "@kubdee/sdk/hooks/use-allowed-branches";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@kubdee/ui/shadcn/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@kubdee/ui/shadcn/dropdown-menu";
import { UserAvatar } from "./user-avatar";

export interface NavItem {
  title: string;
  href: string;
  icon: LucideIcon;
  /** Only match exact URL (don't match children) */
  exact?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

interface AppSidebarProps {
  /** Module ID — resolves icon + color from platform modules API (single source of truth) */
  moduleId?: string;
  title: string;
  /** Override icon (used when moduleId is not in DB, e.g. admin) */
  icon?: LucideIcon;
  /** Override color (used when moduleId is not in DB, e.g. admin) */
  color?: string;
  navGroups: NavGroup[];
}

export function AppSidebar({
  moduleId,
  title,
  icon: AppIconOverride,
  color: colorOverride,
  navGroups,
}: AppSidebarProps) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { theme, setTheme } = useTheme();
  const { user, logout, currentCompanyId, switchCompany } = useAuth();
  const { currentBranchId, setCurrentBranch } = useAuthStore();
  const selectedBranchId = currentBranchId ?? "all";
  const setSelectedBranchId = (id: string) =>
    setCurrentBranch(id === "all" ? null : id);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Fetch companies, branches, modules from API
  const { data: companiesRes } = useCompanies();
  const { data: branchesRes } = useBranches(currentCompanyId);
  const { data: modulesRes } = useCompanyModules(currentCompanyId);

  const { allowedBranchIds } = useAllowedBranches();

  const companies = companiesRes?.data ?? [];
  const allBranches = branchesRes?.data ?? [];
  // Filter branches by user's allowed branches (null = show all)
  const branches =
    allowedBranchIds === null
      ? allBranches
      : allBranches.filter((b) => allowedBranchIds.includes(b.id));
  const selectedCompany =
    companies.find((c) => c.id === currentCompanyId) || companies[0];
  const selectedBranch = branches.find((b) => b.id === selectedBranchId);

  // Fetch platform modules (name from DB)
  const { data: platformModulesRes } = usePlatformModules();
  const platformModules = platformModulesRes?.data ?? [];

  // Fetch tenant-level enabled modules
  const { data: tenantModulesRes } = useTenantModules();
  const tenantEnabledModules = tenantModulesRes?.data ?? null; // null = not loaded yet (show all)

  // Build name lookup from platform modules DB
  const platformModuleMap = new Map(platformModules.map((m) => [m.id, m]));

  // Resolve icon + color from platform module (single source of truth)
  const currentPlatformModule = moduleId
    ? platformModuleMap.get(moduleId)
    : undefined;
  const AppIcon =
    AppIconOverride ??
    (currentPlatformModule
      ? getModuleIcon(currentPlatformModule.icon)
      : undefined);
  const color = colorOverride ?? currentPlatformModule?.color ?? "#2490EF";

  // Build app list from company modules + platform modules, filtered by tenant-level
  const apiModules = modulesRes?.data ?? [];
  const apps = apiModules
    .filter((m: any) => {
      if (!m.isEnabled || m.module === MODULES.CORE) return false;
      // Filter by tenant-enabled modules (if loaded; null = show all for graceful degradation)
      const moduleId = m.moduleId || m.module;
      if (tenantEnabledModules && !tenantEnabledModules.includes(moduleId))
        return false;
      return true;
    })
    .map((m: any) => {
      const moduleId = m.moduleId || m.module;
      const platform = platformModuleMap.get(moduleId);
      return {
        id: moduleId,
        name: platform?.name || moduleId,
        icon: getModuleIcon(platform?.icon || moduleId),
        color: platform?.color ?? getModuleColor(moduleId),
        href: platform?.routePath ?? `/${moduleId}`,
        sortOrder: platform?.sortOrder ?? 999,
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Always include settings
  if (!apps.some((a) => a.id === "settings")) {
    apps.push({
      id: "settings",
      name: "Settings",
      icon: SettingsIcon,
      color: "#6B7280",
      href: "/settings/dashboard",
      sortOrder: 9999,
    });
  }

  const displayUser = {
    name: user?.name || "ผู้ใช้",
    email: user?.email || "",
    avatarUrl: user?.avatarUrl || null,
  };

  async function handleLogout() {
    setIsLoggingOut(true);
    await logout();
    // logout() handles redirect to /login
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            {/* Header: Logo + Company + Branch + Dropdown trigger */}
            <DropdownMenu>
              <DropdownMenuTrigger className="w-full">
                <div className="flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-0">
                  {/* App Logo */}
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] text-white"
                    style={{ backgroundColor: color }}
                  >
                    {AppIcon ? (
                      <AppIcon className="h-5 w-5" strokeWidth={1.8} />
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                        <line x1="12" y1="22.08" x2="12" y2="12" />
                      </svg>
                    )}
                  </div>

                  {/* App Name + Branch · Company */}
                  <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-semibold">{title}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {selectedBranch ? (
                        <>
                          {selectedBranch.name} ·{" "}
                          <span className="truncate">
                            {selectedCompany?.name}
                          </span>
                        </>
                      ) : (
                        selectedCompany?.name || "เลือกบริษัท"
                      )}
                    </span>
                  </div>

                  {/* ChevronsUpDown icon */}
                  <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
                </div>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                side="bottom"
                align="start"
                sideOffset={8}
                className="w-auto min-w-[180px] rounded-xl p-1"
              >
                {/* หน้าหลัก */}
                <DropdownMenuItem
                  className="gap-2 rounded-lg"
                  onClick={() => (window.location.href = "/desk")}
                >
                  <Home className="h-4 w-4 text-muted-foreground" />
                  <span>หน้าหลัก</span>
                </DropdownMenuItem>

                {/* แอป — Sub menu */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="gap-2 rounded-lg">
                    <Grid2x2 className="h-4 w-4 text-muted-foreground" />
                    <span>แอป</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    sideOffset={12}
                    className="rounded-xl p-1 z-[100]"
                  >
                    {apps.map((app) => (
                      <DropdownMenuItem
                        key={app.id}
                        className="gap-2.5 rounded-lg [&[data-highlighted]_[data-app-icon]]:text-white"
                        onClick={() => (window.location.href = app.href)}
                      >
                        <div
                          data-app-icon=""
                          className="flex h-7 w-7 items-center justify-center rounded-lg text-white [&_svg]:text-white"
                          style={{ backgroundColor: app.color }}
                        >
                          <app.icon className="h-3.5 w-3.5" strokeWidth={2} />
                        </div>
                        <span>{app.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                {/* บริษัท — Sub menu */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="gap-2 rounded-lg">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>บริษัท</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    sideOffset={12}
                    className="rounded-xl p-1 z-[100]"
                  >
                    {companies.map((company) => (
                      <DropdownMenuItem
                        key={company.id}
                        className="gap-2 rounded-lg"
                        onClick={() => switchCompany(company.id)}
                      >
                        {company.id === currentCompanyId ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                        <span>{company.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                {/* สาขา — Sub menu */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="gap-2 rounded-lg">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>สาขา</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    sideOffset={12}
                    className="rounded-xl p-1 z-[100]"
                  >
                    {/* Only show "All branches" if user has access to all */}
                    {allowedBranchIds === null && (
                      <DropdownMenuItem
                        className="gap-2 rounded-lg"
                        onClick={() => setSelectedBranchId("all")}
                      >
                        {selectedBranchId === "all" ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                        <span>ทุกสาขา</span>
                      </DropdownMenuItem>
                    )}
                    {branches.map((branch) => (
                      <DropdownMenuItem
                        key={branch.id}
                        className="gap-2 rounded-lg"
                        onClick={() => setSelectedBranchId(branch.id)}
                      >
                        {branch.id === selectedBranchId ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                        <span>{branch.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                {/* โหมดมืด */}
                <DropdownMenuItem
                  className="gap-2 rounded-lg"
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                >
                  {theme === "dark" ? (
                    <Sun className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Moon className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span>{theme === "dark" ? "โหมดสว่าง" : "โหมดมืด"}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const currentFullPath =
                    pathname +
                    (searchParams.toString()
                      ? `?${searchParams.toString()}`
                      : "");
                  const bestMatch = group.items
                    .filter((i) => {
                      const [iPath, iQuery] = i.href.split("?");
                      if (i.exact)
                        return iQuery
                          ? currentFullPath === i.href
                          : pathname === iPath;
                      const pathMatch =
                        pathname === iPath || pathname.startsWith(iPath + "/");
                      if (!pathMatch) return false;
                      if (iQuery) {
                        const hrefParams = new URLSearchParams(iQuery);
                        return Array.from(hrefParams.entries()).every(
                          ([k, v]) => searchParams.get(k) === v
                        );
                      }
                      return true;
                    })
                    .sort((a, b) => b.href.length - a.href.length)[0]?.href;
                  const isActive = item.href === bestMatch;
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton
                        isActive={isActive}
                        tooltip={item.title}
                      >
                        <Link
                          to={item.href}
                          prefetch="none"
                          className="flex items-center gap-2 w-full"
                        >
                          <item.icon className="h-4 w-4" strokeWidth={1.7} />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger className="w-full">
                <div className="flex w-full items-center gap-2 overflow-hidden rounded-xl p-2 text-left text-sm transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-0">
                  <UserAvatar
                    name={displayUser.name}
                    email={displayUser.email}
                    avatarUrl={displayUser.avatarUrl}
                    className="h-7 w-7"
                    fallbackClassName="text-[11px] font-medium"
                  />
                  <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate text-[13px] font-medium">
                      {displayUser.name}
                    </span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {displayUser.email}
                    </span>
                  </div>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="start"
                sideOffset={8}
                className="w-auto min-w-[180px] rounded-xl p-1"
              >
                <DropdownMenuItem
                  className="rounded-lg"
                  onClick={() => (window.location.href = "/me")}
                >
                  <User className="h-3.5 w-3.5" />
                  โปรไฟล์
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="rounded-lg"
                  disabled={isLoggingOut}
                  onClick={handleLogout}
                >
                  {isLoggingOut ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <LogOut className="h-3.5 w-3.5" />
                  )}
                  {isLoggingOut ? "กำลังออกจากระบบ..." : "ออกจากระบบ"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
